"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Check, ChevronRight, ChevronLeft, Send, User, Briefcase, Calendar, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const inputStyles = "flex h-12 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";
const textareaStyles = "flex min-h-[120px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 resize-y";
const checkboxStyles = "h-5 w-5 rounded border-input text-primary focus:ring-ring focus:ring-offset-background";
export default function VolunteerApplication() {
  return <section id="volunteer-application" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute top-[40%] -left-[10%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container max-w-3xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-primary font-serif mb-4">
            Join Our Mission
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Become a part of the COHCC Foundation family. Your time and skills can help expand economic opportunity for our community.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-xl border border-border/50 overflow-hidden">
          <FormProvider formId="volunteer_application_form_get_involved" sectionName="get-involved" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          interest_areas: [],
          availability: "",
          skills: "",
          motivation: "",
          consent: false
        }} validationRules={{
          full_name: {
            required: "Full name is required",
            minLength: 2
          },
          email: {
            required: "Email is required",
            pattern: "email"
          },
          phone: {
            required: "Phone number is required",
            minLength: 10
          },
          interest_areas: {
            required: "Please select at least one area of interest"
          },
          availability: {
            required: "Please select your availability"
          },
          consent: {
            required: "You must agree to the privacy policy"
          }
        }} submitText="Submit Application" successMessage="Thank you for your application! We will be in touch shortly.">
            <VolunteerFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function VolunteerFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit
  } = useWvcForm();
  const {
    trigger,
    watch
  } = useFormContext();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = async () => {
    let fieldsToValidate: string[] = [];
    if (step === 1) {
      fieldsToValidate = ["full_name", "email", "phone"];
    } else if (step === 2) {
      fieldsToValidate = ["interest_areas", "skills", "motivation"];
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({
        top: document.getElementById("volunteer-application")?.offsetTop,
        behavior: 'smooth'
      });
    }
  };
  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({
      top: document.getElementById("volunteer-application")?.offsetTop,
      behavior: 'smooth'
    });
  };
  if (isSubmitted && !submitError) {
    return <div className="p-12 text-center flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500">
          <Check className="w-10 h-10" />
        </div>
        <h3 className="text-2xl font-bold text-primary font-serif mb-2">Application Received!</h3>
        <p className="text-muted-foreground text-lg mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={() => window.location.reload()} variant="outline" className="min-w-[200px]">
          Submit Another Application
        </Button>
      </div>;
  }
  return <div className="flex flex-col h-full">
      {}
      <div className="bg-muted/30 p-6 border-b border-border">
        <div className="flex justify-between items-center mb-4 text-sm font-medium text-muted-foreground">
          <span>Step {step} of {totalSteps}</span>
          <span className="text-primary font-semibold">
            {step === 1 && "Personal Details"}
            {step === 2 && "Skills & Interests"}
            {step === 3 && "Availability & Consent"}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <Form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        <div className="p-6 md:p-10 space-y-8 flex-1">
          
          {}
          {step === 1 && <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary/10 rounded-full text-primary">
                  <User className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary">Personal Information</h3>
                  <p className="text-sm text-muted-foreground">Tell us a bit about yourself.</p>
                </div>
              </div>

              <FormField name="full_name" render={({
            field
          }) => <FormItem>
                    <FormLabel>Full Name <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <input {...field} placeholder="Jane Doe" className={inputStyles} type="text" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField name="email" render={({
              field
            }) => <FormItem>
                      <FormLabel>Email Address <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <input {...field} placeholder="jane@example.com" className={inputStyles} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField name="phone" render={({
              field
            }) => <FormItem>
                      <FormLabel>Phone Number <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <input {...field} placeholder="(555) 123-4567" className={inputStyles} type="tel" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
            </div>}

          {}
          {step === 2 && <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary/10 rounded-full text-primary">
                  <Briefcase className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary">Skills & Interests</h3>
                  <p className="text-sm text-muted-foreground">How would you like to contribute?</p>
                </div>
              </div>

              <FormField name="interest_areas" render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-base">Areas You'd Like to Volunteer In <span className="text-destructive">*</span></FormLabel>
                    <FormDescription className="mb-3">
                      Select one or more areas that interest you.
                    </FormDescription>
                    <FormControl>
                      <ToggleGroup type="multiple" variant="outline" className="flex flex-wrap justify-start gap-3" value={field.value || []} onValueChange={field.onChange}>
                        {["Entrepreneurship Programs", "Leadership Development", "Youth & Education", "Workforce Innovation", "Events & Community Outreach"].map((interest, i) => <ToggleGroupItem key={interest} value={interest} data-index={i} className="px-4 py-2 h-auto text-sm border-border data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground data-[state=on]:border-secondary hover:bg-muted hover:text-foreground transition-all">
                            {interest}
                          </ToggleGroupItem>)}
                      </ToggleGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField name="skills" render={({
            field
          }) => <FormItem>
                    <FormLabel>Relevant Skills / Experience</FormLabel>
                    <FormControl>
                      <textarea {...field} placeholder="e.g., marketing, tutoring, event planning, bilingual (Spanish/English)..." className={textareaStyles} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField name="motivation" render={({
            field
          }) => <FormItem>
                    <FormLabel>Why Do You Want to Volunteer?</FormLabel>
                    <FormControl>
                      <textarea {...field} placeholder="Share your motivation for joining the COHCC Foundation..." className={textareaStyles} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>}

          {}
          {step === 3 && <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary/10 rounded-full text-primary">
                  <SafeHydrate><Calendar className="w-6 h-6" /></SafeHydrate>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary">Availability & Consent</h3>
                  <p className="text-sm text-muted-foreground">Final details to complete your application.</p>
                </div>
              </div>

              <FormField name="availability" render={({
            field
          }) => <FormItem>
                    <FormLabel>Availability <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <div className="relative">
                        <select {...field} className={`${inputStyles} appearance-none cursor-pointer`}>
                          <option value="" disabled>Choose your availability</option>
                          <option value="Weekdays">Weekdays</option>
                          <option value="Weekends">Weekends</option>
                          <option value="Evenings">Evenings</option>
                          <option value="Flexible">Flexible</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                          <ChevronRight className="w-4 h-4 rotate-90" />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <div className="bg-muted/30 p-6 rounded-lg border border-border">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-secondary/20 rounded-full text-secondary-foreground shrink-0">
                    <HeartHandshake className="w-5 h-5" />
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-semibold text-primary">Community Agreement</h4>
                    <p className="text-sm text-muted-foreground">
                      By submitting this application, you agree to uphold the values of the COHCC Foundation and treat all community members with respect and dignity.
                    </p>
                    
                    <FormField name="consent" render={({
                  field
                }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <input type="checkbox" checked={field.value} onChange={field.onChange} className={`${checkboxStyles} mt-1 cursor-pointer`} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              I agree to the privacy policy and to be contacted regarding volunteer opportunities. <span className="text-destructive">*</span>
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>} />
                  </div>
                </div>
              </div>
            </div>}

          {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm animate-in fade-in">
              {submitError}
            </div>}
        </div>

        {}
        <div className="p-6 md:p-10 bg-muted/10 border-t border-border flex justify-between items-center">
          <Button type="button" variant="ghost" onClick={prevStep} disabled={step === 1 || isSubmitting} className={step === 1 ? "invisible" : ""}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {step < totalSteps ? <Button type="button" onClick={nextStep} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Next Step
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-secondary text-secondary-foreground hover:bg-secondary/90 min-w-[140px]">
              {isSubmitting ? "Submitting..." : <>
                  Submit Application
                  <Send className="w-4 h-4 ml-2" />
                </>}
            </Button>}
        </div>
      </Form>
    </div>;
}